import React, { useEffect, useState } from 'react';
import { ILoginProps } from '../../types/propTypes';
import { Helmet } from 'react-helmet';
import { ILoginForm } from '../../models/login';
import { Formik, Field } from 'formik';
import { Form, Button } from 'react-bootstrap';
import { ReactstrapInput, ReactstrapFormikPasswordInput } from '../../components/reactstrap-formik';
import { LoginValidationSchema } from '../../validation/login/login-validation';
import { Common } from '../../utils/constants';
import SocialMediaLoginForm from './SocialMediaLoginForm';
import { useCookies } from 'react-cookie';
import images from '../../assets/images';
import { companyNameReplace } from '../../utils';
import { useTranslation } from 'react-i18next';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormFeedback } from 'reactstrap';
import { getAPIKey } from '../../services/login/login-service';
import { store } from '../../store';
import RecaptchaV2Net from '../../components/RecaptchaV2Net';

const LoginForm = ({ onChangeStage, handleSubmit, handleChangeFormValues }: ILoginProps) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isRecaptchaV2, setIsRecaptchaV2] = useState(false);

  const [cookies, setCookie] = useCookies(['driver_remember', 'driver_email', 'driver_password']);

  const [siteKey, setSiteKey] = useState<string>('');
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  useEffect(() => {
    getRecaptchaSiteKey();
  }, []);

  const getRecaptchaSiteKey = async () => {
    const config = await getAPIKey({
      code: Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY,
    });
    const siteKey = config.data.find(
      (item) => item.reference_code === Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY,
    )?.value;
    setSiteKey(siteKey ?? '');
  };

  const rememberValue = cookies.driver_remember === 'true';
  const parameters = new URLSearchParams(window.location.search);

  const initialState: ILoginForm = {
    email: rememberValue
      ? cookies.driver_email
      : parameters?.get('email') && parameters?.get('email') !== 'undefined'
        ? `${parameters.get('email')}`
        : '',
    password: rememberValue ? cookies.driver_password : '',
    rememberMe: rememberValue,
    recaptchaCode: '',
  };

  const handleLoginSubmit = async (value: ILoginForm, actions) => {
    actions.setFieldValue('recaptchaCode', '');
    actions.setFieldTouched('recaptchaCode', false);
    const date = new Date();
    date.setDate(date.getDate() + 30);

    setCookie('driver_remember', value.rememberMe, {
      path: '/',
      expires: date,
    });

    if (value.rememberMe) {
      setCookie('driver_email', value.email.toLowerCase(), {
        path: '/',
        expires: date,
      });
      setCookie('driver_password', value.password, {
        path: '/',
        expires: date,
      });
    }

    try {
      let token = '';
      if (!isRecaptchaV2 && executeRecaptcha) {
        token = await executeRecaptcha('login');
        if (!token) {
          setIsRecaptchaV2(true);
          return;
        }
        value.recaptchaCode = token;
        handleSubmit(value, isRecaptchaV2, setIsRecaptchaV2);
        return;
      }
      handleSubmit(value, isRecaptchaV2, setIsRecaptchaV2);
    } catch {
      setIsRecaptchaV2(true);
    } finally {
      if (isRecaptchaV2 && recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  const { t } = useTranslation();
  return (
    <>
      <Helmet>
        <title>{companyNameReplace(t('login.title'))}</title>
      </Helmet>
      <h1 className="title">
        <img src={images.logoIconPNG} alt={companyNameReplace(t<string>('login.label.logo'))} />
        {t('login.header')}
      </h1>
      <Formik
        key={isRecaptchaV2 ? 'v2' : 'v3'}
        initialValues={initialState}
        onSubmit={handleLoginSubmit}
        validationSchema={LoginValidationSchema(t, isRecaptchaV2)}
      >
        {({ handleSubmit, values, setFieldValue, setFieldTouched, touched, errors }) => {
          return (
            <form className="form-secondary" onSubmit={handleSubmit} autoComplete="off" noValidate>
              <Field
                type="text"
                name="email"
                id="Email"
                label={t('login.form.email')}
                component={ReactstrapInput}
                maxLength={50}
                onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  handleChangeFormValues(e);
                }}
                autoComplete="username"
              />
              <Field
                type="password"
                name="password"
                id="Password"
                label={t('login.form.password')}
                autoComplete="off"
                component={ReactstrapFormikPasswordInput}
              />
              <input type="hidden" value={values.recaptchaCode} name="recaptchaCode" />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {isRecaptchaV2 && siteKey && (
                  <RecaptchaV2Net
                    ref={recaptchaRef}
                    sitekey={siteKey}
                    onChange={(value) => {
                      setFieldValue('recaptchaCode', value);
                      setFieldTouched('recaptchaCode', true);
                    }}
                    hl={
                      store?.getState()?.UserData?.language?.code
                        ? store?.getState()?.UserData?.language?.code
                        : store?.getState()?.LanguageInfo?.defaultLanguage?.code
                    }
                    key={
                      store?.getState()?.UserData?.language?.code
                        ? store?.getState()?.UserData?.language?.code
                        : store?.getState()?.LanguageInfo?.defaultLanguage?.code
                    }
                  />
                )}

                <div className="text-left codeFixWidth">
                  {touched.recaptchaCode && errors.recaptchaCode !== '' && values.recaptchaCode == '' && (
                    <FormFeedback className="pb-0 error invalid-feedback d-block" type="invalid">
                      {errors.recaptchaCode}
                    </FormFeedback>
                  )}
                </div>
              </div>
              <Form.Group className="form-group pt-3">
                <Button type="submit" className="btn btn-primary w-100">
                  {t('login.button.signin')}
                </Button>
              </Form.Group>
              <div className="form-group pb-2">
                <div className="custom-control custom-checkbox custom-control-inline">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="remember-me"
                    name="rememberMe"
                    checked={values.rememberMe}
                    onChange={() => setFieldValue('rememberMe', !values.rememberMe)}
                  />
                  <label className="custom-control-label pb-0" htmlFor="remember-me">
                    {t('login.label.rememberMe')}
                  </label>
                </div>
                <a
                  href="#"
                  className="forgot-password float-right"
                  title={t<string>('login.button.forgotPassword')}
                  onClick={(e) => {
                    e.preventDefault();
                    onChangeStage(Common.AuthStage.FORGOT_PASSWORD);
                  }}
                >
                  {t('login.button.forgotPassword')}
                </a>
              </div>
              <SocialMediaLoginForm />
              <div className="clearfix border-top-1 pt-4">
                <p className="sign-up">
                  {t('login.label.newAccount')}{' '}
                  <a
                    href="#"
                    title={t<string>('login.button.signup')}
                    onClick={(e) => {
                      e.preventDefault();
                      onChangeStage(Common.AuthStage.SIGNUP);
                    }}
                    className="signup-btn"
                  >
                    {t('login.button.signup')}
                  </a>
                </p>
              </div>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export default LoginForm;
